// Stands in for the `unload` package (aliased in vite.config.ts), which broadcast-channel uses to
// close okta-auth-js's tab-sync and leader-election channels when the page goes away. The real
// package also adds a window `unload` listener, which Chrome now blocks by default and reports
// as a permissions-policy violation on every page load. That listener only mattered for pages
// inside an iframe, and AgencyOS isn't framed; `beforeunload` covers a top-level window.
//
// Same API as unload@2.4.1, browser path only.

type Listener = () => unknown;

const listeners = new Set<Listener>();
let listening = false;

function startListening(): void {
  if (listening) return;
  listening = true;
  window.addEventListener('beforeunload', () => void runAll(), true);
}

export function add(fn: Listener): { remove: () => boolean; run: () => unknown } {
  startListening();
  if (typeof fn !== 'function') {
    throw new Error('Listener is no function');
  }
  listeners.add(fn);
  return {
    remove: () => listeners.delete(fn),
    run: () => {
      listeners.delete(fn);
      return fn();
    },
  };
}

export function runAll(): Promise<unknown[]> {
  const results: unknown[] = [];
  listeners.forEach((fn) => {
    results.push(fn());
    listeners.delete(fn);
  });
  return Promise.all(results);
}

export function removeAll(): void {
  listeners.clear();
}

export function getSize(): number {
  return listeners.size;
}
